import React from 'react';
import {
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants';
import { ROUTE_GROUPS, ROUTE_NAMES } from '@atlassian/jira-common-constants/src/spa-routes';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project';
import {
	NavigateBackProvider,
	useNavigateBack,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-back-button';
import {
	AsyncGlobalAppsSettingsSidebar,
	APPS_SETTINGS_ROUTE_NAMES,
	useGlobalSettingsConnectItemActive,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-apps-settings';
import {
	AsyncGlobalIssuesSettingsSidebar,
	ISSUES_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-issues-settings';
import { AsyncGlobalProductsSettingsSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-products-settings';
import {
	AsyncGlobalProjectsSettingsSidebar,
	PROJECTS_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-projects-settings';
import {
	AsyncGlobalSystemSettingsSidebar,
	SYSTEM_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-global-settings-system-settings';
import {
	AsyncPlanSettingsSidebar,
	PLAN_SETTINGS_ROUTE_NAMES,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-plan-settings';
import { AsyncBusinessProjectSettingsSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-business-settings';
import { Skeleton as ProjectSettingsSkeleton } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-common/src/common/ui/skeleton';
import { PROJECT_SETTINGS_ROUTE_NAMES } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-common/src/constants';
import { AsyncJpdProjectSettingsSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-jpd-settings';
import { AsyncSoftwareProjectSettingsSidebar } from '@atlassian/jira-navigation-apps-sidebar-nav4-project-settings-software-settings';
import AsyncSidebarContent from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebar-content/src/async';
import { useCurrentRoute, usePathParam } from '@atlassian/jira-platform-router-utils';
import { JsmProjectSettingsSidebarSelector } from './ui/jsm-project-settings';

const ProjectSettingsSidebarSelector = () => {
	const { data: project, loading, error } = useProject();

	if (loading) {
		return <ProjectSettingsSkeleton />;
	}

	if (project?.type === CORE_PROJECT) {
		return <AsyncBusinessProjectSettingsSidebar />;
	}

	if (project?.type === SOFTWARE_PROJECT) {
		return <AsyncSoftwareProjectSettingsSidebar />;
	}

	if (project?.type === SERVICE_DESK_PROJECT) {
		return <JsmProjectSettingsSidebarSelector project={project} />;
	}

	if (error) {
		throw error;
	}

	throw new Error(`No project settings sidebar found for the project type: ${project?.type}`);
};

const InternalSidebarSelector = () => {
	const route = useCurrentRoute();
	const [section] = usePathParam('section');
	const { saveCurrentRoute } = useNavigateBack();
	const isGlobalSettingsConnectItemActive = useGlobalSettingsConnectItemActive();

	// Project settings sidebars
	if (route.group === ROUTE_GROUPS.PROJECT_SETTINGS_SOFTWARE) {
		return <AsyncSoftwareProjectSettingsSidebar />;
	}
	if (route.group === ROUTE_GROUPS.PROJECT_SETTINGS_CORE) {
		return <AsyncBusinessProjectSettingsSidebar />;
	}
	if (route.name === ROUTE_NAMES.POLARIS_IDEAS && section === 'settings') {
		return <AsyncJpdProjectSettingsSidebar />;
	}
	if (
		PROJECT_SETTINGS_ROUTE_NAMES.has(route.name) ||
		route.group === ROUTE_GROUPS.PROJECT_SETTINGS_SERVICEDESK
	) {
		return <ProjectSettingsSidebarSelector />;
	}

	// Global settings sidebars
	if (PROJECTS_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncGlobalProjectsSettingsSidebar />;
	}
	if (ISSUES_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncGlobalIssuesSettingsSidebar />;
	}
	if (APPS_SETTINGS_ROUTE_NAMES.has(route.name) || isGlobalSettingsConnectItemActive) {
		return <AsyncGlobalAppsSettingsSidebar />;
	}
	if (route.name === ROUTE_NAMES.PRODUCTS_JSW_CONFIGURATION) {
		return <AsyncGlobalProductsSettingsSidebar />;
	}
	if (SYSTEM_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncGlobalSystemSettingsSidebar />;
	}

	// Plan settings sidebars
	if (PLAN_SETTINGS_ROUTE_NAMES.has(route.name)) {
		return <AsyncPlanSettingsSidebar />;
	}

	// Default sidebar
	// the route to navigate back to will be whichever route was last displayed
	// with the default sidebar on
	saveCurrentRoute();
	return <AsyncSidebarContent />;
};

export const SidebarSelector = () => (
	<NavigateBackProvider>
		<InternalSidebarSelector />
	</NavigateBackProvider>
);
